import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Auto-logout after this many milliseconds of NO user activity at all
// (no mouse movement, no clicks, no keystrokes, no scrolling, no touches).
const INACTIVITY_LIMIT_MS = 10 * 60 * 1000; // 10 minutes

// Every event that counts as "the user is still here" — matches the
// standard set most apps use for idle detection.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Call this ONE time, from a component that renders inside your
 * <BrowserRouter> (e.g. your top-level App component) so it applies
 * globally across every page — not per-page, since a page swap
 * shouldn't reset the whole idle clock on its own.
 *
 * If the user is not currently logged in, this does nothing at all —
 * it only starts watching once localStorage shows an active session,
 * so it never fires on the login page itself or before anyone signs in.
 */
export default function useInactivityLogout() {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    function isLoggedIn() {
      return localStorage.getItem("isAuthenticated") === "true";
    }

    function performLogout() {
      if (!isLoggedIn()) return; // already logged out, nothing to do

      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("loggedInUser");
      localStorage.removeItem("loggedInRole");
      localStorage.removeItem("role");

      alert("You've been logged out due to 10 minutes of inactivity.");
      navigate("/admin");
    }

    function resetTimer() {
      if (!isLoggedIn()) return; // don't bother tracking a logged-out visitor

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(performLogout, INACTIVITY_LIMIT_MS);
    }

    // Start the clock immediately (covers the case where the tab was
    // already open and logged in before this hook first mounts).
    resetTimer();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimer);
      });
    };
  }, [navigate]);
}
